// apps/web/src/App.tsx

import React, { useState, useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { trpc } from '@/trpc/client';


import { useAuthStore } from './store/authStore';
import superjson from 'superjson';
import { ThemeProvider } from '@/providers/ThemeProvider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster as Sonner } from '@/components/ui/sonner';
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import PublicRoute from '@/components/PublicRoute';
import SeasonalLayer from '@/components/effects/SeasonalLayer';
import { SeasonProvider } from '@/context/SeasonContext';
import PermissionRequestModal from '@/components/PermissionRequestModal';
import { FloatingAssistant } from '@/components/FloatingAssistant';
import ScrollToTop from '@/components/ui/ScrollToTop';
import CookieBanner from '@/components/CookieBanner';
import TransporterVehiclePrompt from '@/components/TransporterVehiclePrompt';
import LandingPage from '@/pages/LandingPage';
import NotFound from '@/pages/NotFound';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import DriverRegister from '@/pages/DriverRegister';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import CreateJob from '@/pages/jobs/CreateJob';
import JobsFeed from '@/pages/jobs/JobsFeed';
import JobDetailsPage from '@/pages/jobs/JobDetailsPage';
import BookingSuccessPage from '@/pages/jobs/BookingSuccessPage';
import DashboardPage from '@/pages/Dashboard';
import ClientDashboard from '@/pages/dashboard/ClientDashboard';
import TransporterDashboard from '@/pages/dashboard/TransporterDashboard';
import FleetDashboard from '@/pages/dashboard/FleetDashboard';
import DriverDashboard from '@/pages/dashboard/DriverDashboard';
import MyVehicles from '@/pages/dashboard/MyVehicles';
import PaymentSuccessPage from '@/pages/payments/PaymentSuccess';
import VerificationsPage from '@/pages/admin/VerificationsPage';
import Profile from '@/pages/dashboard/Profile';
import SettingsPage from '@/pages/dashboard/SettingsPage';
import VerificationPage from '@/pages/onboarding/VerificationPage';
import PendingVerificationPage from '@/pages/onboarding/PendingVerificationPage';
import WalletPage from '@/pages/dashboard/WalletPage';
import PurchaseSuccessPage from '@/pages/dashboard/wallet/PurchaseSuccessPage';
import ReceiptsPage from '@/pages/dashboard/ReceiptsPage';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import UserApprovalPage from '@/pages/admin/UserApprovalPage';
import AuditLogsPage from '@/pages/admin/AuditLogsPage';
import ManageUsersPage from '@/pages/admin/ManageUsersPage';
import AllVehiclesPage from '@/pages/admin/AllVehiclesPage';
import AllJobsPage from '@/pages/admin/AllJobsPage';
import EmailTestPage from '@/pages/admin/EmailTestPage';
import ClientJobTrackingPage from '@/pages/dashboard/ClientJobTrackingPage';
import { httpBatchLink } from '@trpc/client';
import { useSocket } from '@/hooks/useSocket';
import AboutUsPage from '@/pages/AboutUsPage';
import ContactPage from '@/pages/ContactPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import HowItWorksPage from '@/pages/HowItWorksPage';
import FAQPage from '@/pages/FAQPage';
import AllOperationsPage from '@/pages/AllOperationsPage';
import SystemStatusPage from '@/pages/SystemStatusPage';
import { getBaseUrl } from './trpc/client';

// ... existing imports
// ... imports
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';
import RecoveryCompaniesPage from '@/pages/admin/RecoveryCompaniesPage';
import MechanicGaragesPage from '@/pages/admin/MechanicGaragesPage';
import MechanicGaragesPublicPage from '@/pages/MechanicGaragesPublicPage';
import FeedbackAdminPage from '@/pages/admin/FeedbackAdminPage';

// ...
import MaintenancePage from '@/pages/MaintenancePage';
import BlogListPage from '@/pages/blog/BlogListPage';
import BlogPostPage from '@/pages/blog/BlogPostPage';
import AdminBlogPage from '@/pages/admin/blog/AdminBlogPage';
import AdminBlogEditorPage from '@/pages/admin/blog/AdminBlogEditorPage';

const SocketAndTrpcInitializer: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  useSocket({ enableDebugLogs: false });
  const { user, setUser } = useAuthStore();

  // Hydrate user state from server to ensure fresh status (e.g. after verification)
  trpc.user.getProfile.useQuery(undefined, {
    enabled: !!user,
    onSuccess: (freshUser) => {
      const currentUser = useAuthStore.getState().user;
      if (freshUser && (
        freshUser.verificationStatus !== currentUser?.verificationStatus ||
        freshUser.onboardingStatus !== currentUser?.onboardingStatus ||
        freshUser.role !== currentUser?.role
      )) {
        console.log('🔄 Hydrating fresh user state from server...');
        setUser(freshUser as any);
      }
    },
    staleTime: 30000
  });

  const { data: settings } = trpc.system.getPublicSettings.useQuery(undefined, {
    refetchInterval: 30000 // Check every 30s
  });

  if (settings?.maintenanceMode === 'true' && user?.role !== 'ADMIN') {
    // Check dates if provided (logic duplicated from backend for frontend UX)
    const now = new Date();
    const start = settings.maintenanceStart ? new Date(settings.maintenanceStart) : null;
    const end = settings.maintenanceEnd ? new Date(settings.maintenanceEnd) : null;

    const isWithinWindow = (!start || now >= start) && (!end || now <= end);

    if (isWithinWindow) {
      return <MaintenancePage />;
    }
  }

  return (
    <>
      <SeasonalLayer />
      <PermissionRequestModal />
      <TransporterVehiclePrompt />
      {children}
    </>
  );
};



// Deep Link Handler Component (must be inside BrowserRouter)
const DeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let listenerHandle: any = null;

    const setupListener = async () => {
      listenerHandle = await CapacitorApp.addListener('appUrlOpen', (data: any) => {
        console.log('🔗 Deep Link Opened:', data.url);

        try {
          const url = new URL(data.url);

          // Universal TX Ref Check (Prioritize explicit types)
          const txRef = url.searchParams.get('tx_ref');

          if (txRef?.startsWith('WALLET-')) {
            console.log('💰 Wallet Payment callback detected, tx_ref:', txRef);
            Browser.close().catch(() => { });
            navigate('/dashboard/wallet', { state: { txRef } });
            return;
          }

          if (data.url.includes('purchase-success')) {
            // Legacy Wallet Return
            console.log('💳 Wallet Payment callback detected (url matches purchase-success)');
            Browser.close().catch(() => { });
            navigate('/dashboard/wallet', { state: { txRef } });

          } else if (data.url.includes('payments/success') || data.url.includes('booking/success') || data.url.includes('payment-success')) {
            // Job Booking Return
            const txRef = url.searchParams.get('tx_ref');
            console.log('📦 Job Payment callback detected, tx_ref:', txRef);

            Browser.close().catch(() => { });
            navigate('/jobs', { state: { txRef } });
          }
        } catch (error) {
          console.error('❌ Deep link parsing error:', error);
        }
      });
    };

    setupListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate]);

  return null;
};

const App = () => {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false, // Prevent excessive refetching
        retry: 1,
      },
    },
  }));

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: getBaseUrl(),
          transformer: superjson,
          headers() {
            const { token } = useAuthStore.getState();
            return {
              Authorization: token ? `Bearer ${token}` : undefined,
            };
          },
        }),
      ],
      transformer: superjson,
    })
  );

  useEffect(() => {
    console.log('🚀 tRPC Client initialized');
  }, [trpcClient]);




  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <SeasonProvider>
            <TooltipProvider>
              <Sonner />
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <DeepLinkHandler />
                <ScrollToTop />
                <SocketAndTrpcInitializer>
                  <Routes>
                    <Route element={<Layout />}>
                      {/* ═══════════════════════════════════════════════════ */}
                      {/* PUBLIC ROUTES (Always accessible) */}
                      {/* ═══════════════════════════════════════════════════ */}
                      <Route path="/" element={Capacitor.isNativePlatform() ? <Navigate to="/login" replace /> : <LandingPage />} />
                      <Route path="/about-us" element={<AboutUsPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                      <Route path="/how-it-works" element={<HowItWorksPage />} />
                      <Route path="/faq" element={<FAQPage />} />
                      <Route path="/operations" element={<AllOperationsPage />} />
                      <Route path="/status" element={<SystemStatusPage />} />
                      <Route path="/jobs" element={<JobsFeed />} />
                      <Route path="/jobs" element={<JobsFeed />} />
                      <Route path="/jobs/:jobId" element={<JobDetailsPage />} />
                      <Route path="/blog" element={<BlogListPage />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />
                      <Route path="/mechanic-garages" element={<MechanicGaragesPublicPage />} />

                      {/* Payment Return Routes (Public to allows mobile app callbacks without browser session) */}
                      <Route path="/dashboard/wallet/purchase-success" element={<PurchaseSuccessPage />} />
                      <Route path="/payments/success" element={<PaymentSuccessPage />} />

                      {/* ═══════════════════════════════════════════════════ */}
                      {/* AUTH ROUTES (Login/Register - Only for non-logged users) */}
                      {/* ═══════════════════════════════════════════════════ */}
                      <Route element={<PublicRoute />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/register/driver" element={<DriverRegister />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                      </Route>

                      {/* ═══════════════════════════════════════════════════ */}
                      {/* PROTECTED ROUTES (Require authentication) */}
                      {/* ═══════════════════════════════════════════════════ */}
                      <Route element={<ProtectedRoute />}>
                        {/* Onboarding routes (accessible even when PENDING) */}
                        <Route path="/onboarding/verify" element={<VerificationPage />} />
                        <Route path="/onboarding/pending" element={<PendingVerificationPage />} />

                        {/* Dashboard routes (require VERIFIED status) */}
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/dashboard/client" element={<ClientDashboard />} />
                        <Route path="/dashboard/transporter" element={<TransporterDashboard />} />
                        <Route path="/dashboard/fleet" element={<FleetDashboard />} />
                        <Route path="/dashboard/driver" element={<DriverDashboard />} />
                        <Route path="/dashboard/my-vehicles" element={<MyVehicles />} />
                        <Route path="/dashboard/profile" element={<Profile />} />
                        <Route path="/dashboard/settings" element={<SettingsPage />} />
                        <Route path="/dashboard/wallet" element={<WalletPage />} />
                        <Route path="/dashboard/receipts" element={<ReceiptsPage />} />

                        <Route path="/dashboard/jobs/:jobId/track" element={<ClientJobTrackingPage />} />
                        <Route path="/dashboard/booking/success" element={<BookingSuccessPage />} />

                        {/* Job management */}
                        <Route path="/jobs/create" element={<CreateJob />} />



                        {/* Admin routes */}
                        <Route path="/admin" element={<AdminLayout />}>
                          <Route path="dashboard" element={<AdminDashboardPage />} />
                          <Route path="users/approval" element={<UserApprovalPage />} />
                          <Route path="verifications" element={<VerificationsPage />} />
                          <Route path="audit-logs" element={<AuditLogsPage />} />
                          <Route path="users" element={<ManageUsersPage />} />
                          <Route path="vehicles" element={<AllVehiclesPage />} />
                          <Route path="jobs" element={<AllJobsPage />} />
                          <Route path="email-test" element={<EmailTestPage />} />
                          <Route path="email-test" element={<EmailTestPage />} />
                          <Route path="settings" element={<AdminSettingsPage />} />
                          <Route path="blog" element={<AdminBlogPage />} />
                          <Route path="blog/create" element={<AdminBlogEditorPage />} />
                          <Route path="blog/edit/:slug" element={<AdminBlogEditorPage />} />
                          <Route path="recovery-companies" element={<RecoveryCompaniesPage />} />
                          <Route path="mechanic-garages" element={<MechanicGaragesPage />} />
                          <Route path="feedback" element={<FeedbackAdminPage />} />
                        </Route>
                      </Route>

                      {/* ═══════════════════════════════════════════════════ */}
                      {/* 404 NOT FOUND */}
                      {/* ═══════════════════════════════════════════════════ */}
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Routes>

                  {/* Floating Assistant Dock (Chat & Feedback) */}
                  <FloatingAssistant />
                </SocketAndTrpcInitializer>
                {/* Cookie Consent Banner — rendered outside SocketAndTrpcInitializer intentionally
                    so it requires no auth context and appears on every page */}
                <CookieBanner />
              </BrowserRouter>
            </TooltipProvider>
          </SeasonProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
};

export default App;